import type { Ref } from 'vue'

import { AxiosError } from 'axios'
import { computed, readonly, ref } from 'vue'

import type { UserProfile } from '@/types'
import type { WorkspaceListDTO } from '@/types/workspace'

import { eventService } from '@/services/eventService'
import userService from '@/services/userService'
import { getWorkspaces } from '@/services/workspaceService'

import { useNotifications } from './useNotifications'

// Global state - shared across all instances
const userInfo: Ref<UserProfile | null> = ref(null)
const isLoading = ref(true)
const error: Ref<string | null> = ref(null)

// Workspace state
const userWorkspaces: Ref<WorkspaceListDTO[]> = ref([])
const isLoadingWorkspaces = ref(false)
const workspacesError: Ref<string | null> = ref(null)

const isUserInfoAuthError = (err: unknown) =>
  err instanceof AxiosError &&
  err.response !== undefined &&
  [401, 403, 404].includes(err.response.status)

export function useUser() {
  // Getters
  const isAuthenticated = computed(() => !!userInfo.value)
  const hasRole = computed(() => (role: string) => userInfo.value?.roles?.includes(role) || false)

  // Check if user has any of the given roles
  const hasAnyRole = (roles: string[] | undefined) => {
    if (roles === undefined || !userInfo.value?.roles) {
      return false
    }
    return roles.some((role) => userInfo.value!.roles!.includes(role))
  }

  const hasInternalAccess = computed(() => userInfo.value?.internalAccess === true)

  const fullName = computed(() => {
    if (!userInfo.value) {
      return ''
    }
    const { firstName, lastName } = userInfo.value
    if (firstName && lastName) {
      return `${firstName} ${lastName}`
    }
    if (firstName) {
      return firstName
    }
    if (lastName) {
      return lastName
    }
    return userInfo.value.userName || ''
  })

  const profileIsIncomplete = computed(() => {
    if (!userInfo.value) {
      return false
    }
    const { firstName, lastName, mail } = userInfo.value
    return !firstName || !lastName || !mail
  })

  // Workspace actions
  const fetchUserWorkspaces = async (silent = false) => {
    if (!isAuthenticated.value) {
      return
    }

    isLoadingWorkspaces.value = !silent
    workspacesError.value = null

    try {
      const workspaces = await getWorkspaces()
      userWorkspaces.value = workspaces
    } catch (err) {
      console.error('Error fetching user workspaces:', err)
      workspacesError.value = err instanceof Error ? err.message : 'Unknown error'
    } finally {
      isLoadingWorkspaces.value = false
    }
  }

  const clearUserWorkspaces = () => {
    userWorkspaces.value = []
  }

  /**
   * Sync anonymous favorites from session storage to the backend
   * and clear the session storage after sync
   */
  const syncAnonymousFavorites = async () => {
    const ANONYMOUS_FAVORITES_KEY = 'smu-favorites'
    try {
      const stored = sessionStorage.getItem(ANONYMOUS_FAVORITES_KEY)
      if (!stored) {
        return
      }

      const eventIds: number[] = JSON.parse(stored)
      if (!Array.isArray(eventIds) || eventIds.length === 0) {
        return
      }

      // Sync to backend
      await eventService.syncFavorites(eventIds)

      // Clear session storage after successful sync
      sessionStorage.removeItem(ANONYMOUS_FAVORITES_KEY)
    } catch (err) {
      console.error('Error syncing anonymous favorites:', err)
      // Don't throw - this is a non-critical operation
    }
  }

  // Actions
  const fetchUserInfo = async (silent = false, throwOnError = false) => {
    isLoading.value = !silent
    error.value = null

    try {
      const userData = await userService.getUserProfile()

      if (userData?.roles) {
        userData.roles = [...new Set(userData.roles)]
      }

      // Check if this is a new authentication (was not authenticated before)
      const wasAuthenticated = userInfo.value !== null
      userInfo.value = userData

      // If user just became authenticated (e.g., OAuth callback), sync anonymous favorites
      if (!wasAuthenticated && userData !== null) {
        await syncAnonymousFavorites()
      }
    } catch (err) {
      if (isUserInfoAuthError(err)) {
        console.warn('User info request returned an auth status; clearing local user state.', err)
        clearUserInfo()
        error.value = null
        return
      }

      console.error('Error fetching user info:', err)
      error.value = err instanceof Error ? err.message : 'Unknown error'
      if (throwOnError) {
        throw err
      }
    } finally {
      isLoading.value = false
    }
  }

  const clearUserInfo = () => {
    userInfo.value = null
    clearUserWorkspaces()
  }

  // Product helpers
  const userProducts = computed(() => userInfo.value?.products ?? [])
  const userDegree = computed(() => userInfo.value?.degree ?? null)

  /**
   * Refresh products from Campus for the given semester (or current semester).
   * Updates the products in userInfo after refresh.
   */
  const refreshProducts = async (semesterId?: number) => {
    try {
      const products = await userService.refreshProducts(semesterId)
      if (userInfo.value) {
        userInfo.value = { ...userInfo.value, products }
      }
      return products
    } catch (err) {
      console.error('Error refreshing products:', err)
      throw err
    }
  }

  const logout = async () => {
    const notifications = useNotifications()
    notifications.stopPolling()

    try {
      const success = await userService.logout()
      if (success) {
        clearUserInfo()
        return true
      }

      await notifications.initialize()
      return false
    } catch (err) {
      await notifications.initialize()
      console.error('Error during logout:', err)
      error.value = err instanceof Error ? err.message : 'Unknown error'
      return false
    }
  }

  const login = async (username: string, password: string) => {
    try {
      await userService.login(username, password)
      // fetchUserInfo will sync anonymous favorites since user just became authenticated
      await fetchUserInfo()
    } catch (err) {
      console.error('Error during login:', err)
      error.value = err instanceof Error ? err.message : 'Unknown error'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  return {
    // State
    userInfo: readonly(userInfo),
    isLoading: readonly(isLoading),
    error: readonly(error),
    userWorkspaces: readonly(userWorkspaces),
    isLoadingWorkspaces: readonly(isLoadingWorkspaces),
    workspacesError: readonly(workspacesError),

    // Getters
    isAuthenticated,
    hasRole,
    hasAnyRole,
    hasInternalAccess,
    fullName,
    profileIsIncomplete,
    userProducts,
    userDegree,

    // Actions
    fetchUserInfo,
    clearUserInfo,
    logout,
    login,
    fetchUserWorkspaces,
    clearUserWorkspaces,
    refreshProducts,
  }
}
